import DisposableManager from 'seng-disposable-manager/lib/DisposableManager';
import EventDispatcher from 'seng-event';
import { addEventListener } from 'seng-disposable-event-listener';
import LoadVideoTask from 'task-loader/lib/task/LoadVideoTask';
import type { VideoElementOptions } from './data/type/VideoElementOptions';
import { VideoPreloadValue } from './data/enum/VideoPreloadValue';
import type { VideoData } from './data/type/VideoData';
import { VideoEvent } from './data/event/VideoEvent';

// todo; refactor this super old code.
export default class VideoElement extends EventDispatcher {
  public static readonly noXhrPreloaderSupport: boolean = false;
  private static readonly durationInterval: string = 'duration-interval';

  private intervals: { [key: string]: NodeJS.Timer } = {};
  public container: HTMLElement | null;
  public element: HTMLVideoElement | null;
  private duration: number = 0;
  private currentTime: number = 0;
  private animationFrame!: number;

  // private hasClickInteraction: boolean = false;
  private readonly clickInteractionEventListener!: () => void;
  private canPlayListener!: () => void;
  private handleCanPlayPromise!: ((value: number | PromiseLike<unknown>) => void) | null;
  private readonly onTick: OmitThisParameter<() => void>;
  private readonly disposableManager: DisposableManager = new DisposableManager();

  public isPlaying: boolean = false;
  private preloadedVideos: { [src: string]: string } = {};
  private readonly options: VideoElementOptions = {
    preloadWithXhr: false,
    playsInline: true,
    loop: false,
    muted: false,
    preloadAttribute: VideoPreloadValue.None,
    requireClickListener: true,
  };

  public constructor(container: HTMLElement, options?: VideoElementOptions) {
    super();

    this.options = Object.assign(this.options, options);

    this.container = container;
    this.element = document.createElement('video');
    this.element.width = 640;
    this.element.height = 360;
    this.element.muted = this.options.muted || false;
    this.element.preload = this.options.preloadAttribute || VideoPreloadValue.None;
    this.element.loop = this.options.loop || false;

    if (this.options.playsInline) {
      this.element.setAttribute('playsinline', 'true');
    }

    this.container.appendChild(this.element);
    this.onTick = this.tick.bind(this);

    if (this.options.requireClickListener) {
      this.clickInteractionEventListener = addEventListener(
        window,
        'click',
        this.handleClickInteraction.bind(this),
        true,
      );
    }

    this.disposableManager.add(
      addEventListener(this.element, 'play', this.updatePlayingState.bind(this, true)),
    );

    this.disposableManager.add(
      addEventListener(this.element, 'pause', this.updatePlayingState.bind(this, false)),
    );

    this.disposableManager.add(
      addEventListener(this.element, 'ended', this.onVideoEnded.bind(this)),
    );
  }

  private onVideoEnded() {
    this.updatePlayingState(false);

    if (this.options.onEnded) {
      this.options.onEnded();
    }
  }

  /**
   * @private
   * @method updatePlayingState
   */
  private updatePlayingState(isPlaying: boolean): void {
    this.isPlaying = isPlaying;

    if (this.options.onPlaying) {
      this.options.onPlaying(this.isPlaying);
    }

    if (isPlaying) {
      this.tick();
    }
  }

  private tick() {
    if (this.isPlaying) {
      this.animationFrame = requestAnimationFrame(this.onTick);
    } else if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    if (this.element) {
      const currentTime = this.element.currentTime || 0;

      if (currentTime !== this.currentTime) {
        this.currentTime = currentTime;

        if (this.options.onTimeUpdate) {
          this.options.onTimeUpdate(this.currentTime, this.duration);
        }
      }
    }
  }

  /**
   * @public
   * @method updateSource
   * @returns {void}
   */
  public play(): void {
    if (this.element) {
      // if (this.options.requireClickListener && !this.hasClickInteraction) {
      //   return;
      // }

      this.element.play().catch((error: unknown) => {
        console.warn(error);
      });
    }
  }

  /**
   * @public
   * @description: Will restart the video at a currentTime of 0.
   * @method replay
   */
  public replay(): void {
    this.pause();
    this.seek(0);
    this.play();
  }

  /**
   * @public
   * @description: Will toggle the play/pause state
   * @method togglePlay
   */
  public togglePlay(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * @public
   * @method progress
   */
  public seek(time: number): void {
    if (this.element) {
      this.element.currentTime = time;
    }
  }

  /**
   * @public
   * @description: allows a number between [0-1].
   * @method progress
   */
  public progress(progress: number): void {
    if (this.element) {
      this.element.currentTime = progress * this.element.duration;
    }
  }

  /**
   * @public
   * @description: Sometimes the duration method is called too soon for the API and it returns `NaN`. This method is
   * a work-around then will run an interval until the API is ready for returning it's actual duration.
   * @method getDuration
   */
  public getDuration(): Promise<number> {
    return new Promise((resolve: (value: number | PromiseLike<number>) => void) => {
      this.clearInterval(VideoElement.durationInterval);
      this.intervals[VideoElement.durationInterval] = setInterval(() => {
        if (this.element && this.element.duration > 0) {
          this.duration = this.element.duration;
          this.clearInterval(VideoElement.durationInterval);
          resolve(this.duration);
        }
      }, 60);
    });
  }

  /**
   * @public
   * @method pause
   * @returns {void}
   */
  public pause(): void {
    if (this.element) {
      this.element.pause();
    }
  }

  /**
   * @public
   * @description: the setSrc method is resolved when the video is fully preloaded(preloadWithXhr), or when it's native canplay event is triggered
   * @method set src
   */
  public setSrc(
    video: VideoData,
    preloadWithXhr: boolean = this.options.preloadWithXhr || false,
    srcObject: boolean = false,
  ): Promise<Array<unknown>> {
    this.currentTime = 0;
    this.duration = 0;
    this.updatePlayingState(false);

    if (this.element) {
      // Set poster
      if (video.poster) {
        this.element.poster = video.poster;
      }

      let preloadedVideo = null;

      // Check if the file has been preloaded before
      if (!srcObject) {
        preloadedVideo = this.preloadedVideos[video.src as string];
      }

      if (!preloadedVideo && preloadWithXhr && !srcObject) {
        // Return when ready
        return Promise.all([
          // this.getDuration(),
          this.preload(video).then(() => {
            if (this.element) {
              // Pre-loading videos and setting the blob as a source *might* not work in certain browser, so we use
              // the original
              // source
              this.element.src = VideoElement.noXhrPreloaderSupport
                ? (video.src as string)
                : this.preloadedVideos[video.src as string];

              this.getDuration();
            }
          }),
        ]);
      }

      // Remove canPlayListener before assigning a new one
      if (this.canPlayListener) {
        this.canPlayListener();
      }

      this.canPlayListener = addEventListener(
        this.element,
        'canplay',
        this.handleCanPlay.bind(this),
      );

      // Stop before switching the source
      this.pause();

      // Pre-loading videos and setting the blob as a source *might* not work in certain browser, so we use the original
      // source
      if (srcObject) {
        this.element.srcObject = video.src as MediaStream;
      } else {
        this.element.src = VideoElement.noXhrPreloaderSupport
          ? (video.src as string)
          : (preloadedVideo as string) || (video.src as string);
        this.getDuration();
      }

      this.element.load();

      // Return when ready
      return Promise.all([
        new Promise((resolve: (value: number | PromiseLike<unknown>) => void) => {
          this.handleCanPlayPromise = resolve;
        }),
      ]);
    }

    return Promise.reject(`no video element set`);
  }

  /**
   * @private
   * @method handleCanPlay
   */
  private handleCanPlay(): void {
    if (this.handleCanPlayPromise && this.element) {
      this.handleCanPlayPromise(0);
      this.handleCanPlayPromise = null;

      if (this.options.onReady) {
        this.options.onReady();
      }
    }
  }

  /**
   * @private
   * @method preload
   * @returns {Promise<void>}
   */
  private preload(video: VideoData): Promise<void> {
    if (typeof video.src === 'string') {
      const loadVideoTask = new LoadVideoTask({
        assets: video.src,
        onAssetLoaded: ({ asset }) => {
          this.preloadedVideos[video.src as string] = asset;
        },
      });

      return loadVideoTask.load().then(() => {
        loadVideoTask.dispose();
      });
    }
    return Promise.resolve();
  }

  /**
   * @private
   * @method clearInterval
   */
  private clearInterval(id: string): void {
    if (this.intervals[id]) {
      clearInterval(this.intervals[id]);
    }
  }

  /**
   * @method handleClickInteraction
   * @description User interaction allows for auto play on the audio element!
   */
  private handleClickInteraction() {
    // this.hasClickInteraction = true;

    if (!this.isPlaying) {
      this.play();
      this.pause();
    }

    this.removeClickInteractionListener();
  }

  /**
   * @public
   * @method removeClickInteractionListener
   */
  public removeClickInteractionListener(): void {
    if (this.clickInteractionEventListener) {
      this.clickInteractionEventListener();
    }

    this.dispatchEvent(new VideoEvent(VideoEvent.types.CLICK_INTERACTION_CLICK, {}));
  }

  /**
   * @public
   * @method dispose
   * @returns {void}
   */
  public dispose(): void {
    this.pause();
    this.removeClickInteractionListener();

    this.clearInterval(VideoElement.durationInterval);

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.disposableManager.dispose();

    if (this.canPlayListener) {
      this.canPlayListener();
    }

    if (this.container) {
      this.container = null;
    }

    this.element = null;

    super.dispose();
  }
}
